import { compareAsc, isBefore, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { parseServerDateTime } from '../../../api/parsers/commons-api-parsers';
import { ApiRequestStatus } from '../../../model/api-request';
import { GlobalActions } from '../../../model/global-actions';
import { prepareChangeContact } from '../../../store/data/user';
import {
  clearInsertEmailChangeEmailError,
  clearVerificationEmailChangeEmailError,
  resetChangeEmailState,
  resetInsertEmailChangeEmailRequestState,
  resetVerificationEmailChangeEmailRequestState,
  setConfirmNewEmailChangeEmail,
  setEmailChangeEmail,
  setVerificationEmailChangeEmailCode,
  setVerificationPhoneNumberCodeChangeEmailCode,
  updateCountdownEmail,
  updateEmailAlreadyVerificated,
  updateErrorEmailNotEditable,
  updateOpenDialogChangeEmail,
  updateStepChangeEmail,
} from '../../../store/features/profile/change-email/change-email-slice';
import {
  insertEmailChangeEmail,
  verificationEmailChangeEmail,
} from '../../../store/features/profile/change-email/change-email-thunk';
import {
  EmailNotEditableType,
  StepsChangeEmail,
} from '../../../store/features/profile/change-email/change-email-type';
import {
  messagesForExpiredDocumentError,
  messagesForInsertEmailChangeEmailError,
  messagesForVerificationEmailChangeEmailError,
} from '../../../store/features/profile/change-email/change-email-utils';
import { initializeClock } from '../../../utils/coundown';
import { useAppSelector } from '../../redux';
import { resetPrepareChangeContactState } from '../../../store/data/user';

export interface ChangeEmailDialogLogicCallbacks {
  onChangeEmail: () => void;

  disabledButtonFirstStep: boolean;
  email: string;
  confirmNewEmail: string;
  insertEmailChangeEmailRequestStatus: ApiRequestStatus;

  updateEmail: (value?: string) => void;
  updateConfirmNewEmailChangeEmail: (value?: string) => void;
  handleSubmitInsertEmail: () => void;

  changeEmailVerificationCodeEmail: string;
  changeEmailVerificationCodePhoneNumber: string;
  verificationEmailChangeEmailRequestStatus: ApiRequestStatus;

  handleSubmitVerificationEmail: () => void;
  updateVerificationCodeEmail: (value?: string) => void;
  updateVerificationCodePhoneNumber: (value?: string) => void;

  step: StepsChangeEmail;
  openDialogChangeEmail: boolean;

  closeChangeEmail: () => void;

  closeStep3ChangeEmail: () => void;
  prepareChangeContactRequestStatus: ApiRequestStatus;
  tooManyAttemptsErrorChangeEmail: boolean;
  handleCloseErrorTooManyAttemptsChangeEmail: () => void;

  backToStep1: () => void;
  backToStep2: () => void;

  errorEmailNotEditable: EmailNotEditableType;
  emailAlreadyVerificated: boolean;
  closeErrorEmailNotEditable: () => void;
  buttonDisabled: boolean;

  countdown: string;
  timeoutError: boolean;

  changeEmailDisabled: boolean;
}

export const useChangeEmailDialogLogic = (
  actions: GlobalActions
): ChangeEmailDialogLogicCallbacks => {
  const { showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const intl = useIntl();

  const {
    email,
    confirmNewEmail,
    insertEmailChangeEmailRequestStatus,
    insertEmailChangeEmailError,

    changeEmailVerificationCodeEmail,
    verificationEmailChangeEmailRequestStatus,
    verificationEmailChangeEmailError,
    changeEmailVerificationCodePhoneNumber,

    step,
    errorEmailNotEditable,
    openDialogChangeEmail,
    emailAlreadyVerificated,

    endtimeCountdown,
    countdown,
  } = useAppSelector((state) => state.features.changeEmail);

  const emailAttempts = useAppSelector(
    (state) => state.features.changeEmail.emailAttempts
  );
  const phoneNumberAttempts = useAppSelector(
    (state) => state.features.changeEmail.phoneNumberAttempts
  );

  const {
    signFiles,
    signFilesEmail,
    emailUpdatedDate,
    signFilesPhoneNumber,
    email: oldEmail,
    prepareChangeContactRequestStatus,
    prepareChangeContactError,
    changeContactsAvailable,
    documentiScaduti,
  } = useAppSelector((state) => state.data.user);

  const [tooManyAttemptsErrorChangeEmail, setTooManyAttemptsErrorChangeEmail] =
    useState(false);

  const [buttonDisabled, setButtonDisabled] = useState(
    changeEmailVerificationCodeEmail?.length === 0 ||
      changeEmailVerificationCodePhoneNumber?.length === 0
  );

  const [disabledButtonFirstStep, setDisabledButtonFirstStep] = useState(
    !email || email.length === 0
  );

  //Open dialog change email
  const onChangeEmail = useCallback(() => {
    if (signFilesPhoneNumber) {
      dispatch(updateErrorEmailNotEditable('PHONE_VERIFICATION'));
    } else if (signFiles || signFilesEmail) {
      dispatch(updateErrorEmailNotEditable('OTHER_DOCUMENTS_TO_SIGN'));
    } else if (compareAsc(emailUpdatedDate, subDays(new Date(), 5)) === 1) {
      dispatch(updateErrorEmailNotEditable('LAST_UPDATING_UNTIL_5_DAYS'));
    } else {
      dispatch(updateOpenDialogChangeEmail(true));
    }
  }, [
    dispatch,
    emailUpdatedDate,
    signFiles,
    signFilesEmail,
    signFilesPhoneNumber,
  ]);
  // change Step
  const changeStep = useCallback(
    (newStep: StepsChangeEmail) => {
      dispatch(updateStepChangeEmail(newStep));
    },
    [dispatch]
  );

  //Reset state, reset steps, anche close change email dialog
  const closeChangeEmail = useCallback(() => {
    dispatch(updateOpenDialogChangeEmail(false));
    dispatch(resetChangeEmailState());
  }, [dispatch]);

  // Close step 3 (only for status succeeded)
  const closeStep3ChangeEmail = useCallback(() => {
    dispatch(
      prepareChangeContact({
        contact: email,
        oldContact: oldEmail,
        contactType: 'EMAIL',
      })
    );
    // dispatch(getStatusUtenza());
  }, [dispatch, email, oldEmail]);

  // Close dialog error too many attempts
  const handleCloseErrorTooManyAttemptsChangeEmail = useCallback(() => {
    setTooManyAttemptsErrorChangeEmail(false);
    dispatch(onChangeEmail());
  }, [dispatch, onChangeEmail]);

  // Save mail
  const updateEmail = useCallback(
    (value: string) => {
      setDisabledButtonFirstStep(false);
      dispatch(
        setEmailChangeEmail(
          value === undefined ? '' : value.toLocaleLowerCase()
        )
      );
    },
    [dispatch]
  );
  // Save new mail
  const updateConfirmNewEmailChangeEmail = useCallback(
    (value: string) => {
      setDisabledButtonFirstStep(false);
      dispatch(
        setConfirmNewEmailChangeEmail(
          value === undefined ? '' : value.toLocaleLowerCase()
        )
      );
    },
    [dispatch]
  );

  //Save verification code
  const updateVerificationCodeEmail = useCallback(
    (value: string) => {
      if (buttonDisabled === true) {
        setButtonDisabled(false);
      }
      dispatch(setVerificationEmailChangeEmailCode(value));
    },
    [buttonDisabled, dispatch]
  );

  const updateVerificationCodePhoneNumber = useCallback(
    (value: string) => {
      if (buttonDisabled === true) {
        setButtonDisabled(false);
      }
      dispatch(setVerificationPhoneNumberCodeChangeEmailCode(value));
    },
    [buttonDisabled, dispatch]
  );

  //Submit insert mail
  const handleSubmitInsertEmail = useCallback(() => {
    dispatch(insertEmailChangeEmail());
  }, [dispatch]);
  // Submit verification code
  const handleSubmitVerificationEmail = useCallback(() => {
    dispatch(verificationEmailChangeEmail());
  }, [dispatch]);

  const backToStep1 = useCallback(() => {
    changeStep('INSERIMENTO_DATI');
  }, [changeStep]);

  const backToStep2 = useCallback(() => {
    changeStep('VERIFICA_OTP');
  }, [changeStep]);

  const closeErrorEmailNotEditable = useCallback(() => {
    dispatch(updateErrorEmailNotEditable(undefined));
  }, [dispatch]);

  useEffect(() => {
    if (insertEmailChangeEmailRequestStatus === 'succeeded') {
      changeStep('VERIFICA_OTP');
      dispatch(resetInsertEmailChangeEmailRequestState());
    }
  }, [changeStep, dispatch, insertEmailChangeEmailRequestStatus]);

  useEffect(() => {
    if (insertEmailChangeEmailError) {
      const errorMessages = messagesForInsertEmailChangeEmailError(
        insertEmailChangeEmailError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearInsertEmailChangeEmailError());
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    insertEmailChangeEmailError,
    showToast,
  ]);

  useEffect(() => {
    if (prepareChangeContactRequestStatus === 'succeeded') {
      closeChangeEmail();
    } else if (prepareChangeContactRequestStatus === 'failed') {
      const errorMessages = messagesForExpiredDocumentError(
        prepareChangeContactError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      setTimeout(() => {
        dispatch(resetPrepareChangeContactState());
        closeChangeEmail();
      }, 3000);
    }
  }, [prepareChangeContactRequestStatus, prepareChangeContactError]);

  useEffect(() => {
    if (verificationEmailChangeEmailRequestStatus === 'succeeded') {
      changeStep('RIEPILOGO');
      dispatch(resetVerificationEmailChangeEmailRequestState());
      dispatch(updateEmailAlreadyVerificated(true));
    }
  }, [changeStep, dispatch, verificationEmailChangeEmailRequestStatus]);

  useEffect(() => {
    if (verificationEmailChangeEmailError) {
      if (
        emailAttempts !== 0 &&
        phoneNumberAttempts !== 0 &&
        (verificationEmailChangeEmailError?.type === 'GENERIC_ERROR' ||
          verificationEmailChangeEmailError?.type ===
            'CODICE_DI_VERIFICA_SBAGLIATO' ||
          verificationEmailChangeEmailError?.type ===
            'CODICE_DI_VERIFICA_EMAIL_SBAGLIATO' ||
          verificationEmailChangeEmailError?.type ===
            'CODICE_DI_VERIFICA_CELLULARE_SBAGLIATO')
      ) {
        const errorMessages = messagesForVerificationEmailChangeEmailError(
          verificationEmailChangeEmailError,
          intl
        );
        showToast({
          type: 'error',
          title: errorMessages.title,
          description: errorMessages.description,
          close: closeToast,
        });
        dispatch(clearVerificationEmailChangeEmailError());
      } else if (
        emailAttempts === 0 ||
        verificationEmailChangeEmailError?.type === 'TROPPI_TENTATIVI'
      ) {
        closeChangeEmail();
        setTooManyAttemptsErrorChangeEmail(true);
      }
      dispatch(clearVerificationEmailChangeEmailError());
    }
  }, [
    intl,
    verificationEmailChangeEmailError,
    dispatch,
    formatMessage,
    showToast,
    closeToast,
    closeChangeEmail,
    emailAttempts,
    phoneNumberAttempts,
  ]);

  const [timeoutError, setTimeoutError] = useState(false);

  const setCountdown = useCallback(
    (time: string) => {
      if (openDialogChangeEmail && !time.match('NaN') && !timeoutError) {
        dispatch(updateCountdownEmail(time));
      }
    },
    [dispatch, openDialogChangeEmail, timeoutError]
  );

  const onTimeChanged = useCallback(
    (minutes: number, seconds: number) => {
      setCountdown(
        `${minutes < 10 ? '0' + minutes : minutes}:${
          seconds < 10 ? '0' + seconds : seconds
        } min`
      );
    },
    [setCountdown]
  );
  useEffect(() => {
    const now = new Date();
    if (isBefore(parseServerDateTime(endtimeCountdown), now)) {
      setTimeoutError(true);
    } else {
      setTimeoutError(false);
      initializeClock(endtimeCountdown, onTimeChanged);
    }
  }, [endtimeCountdown, onTimeChanged]);

  useEffect(() => {
    if (countdown.indexOf('00:00') === 0) {
      setTimeoutError(true);
    }
  }, [countdown]);

  const changeEmailDisabled = useMemo(() => {
    return Boolean(documentiScaduti || !changeContactsAvailable);
  }, [changeContactsAvailable, documentiScaduti]);

  return {
    disabledButtonFirstStep,
    email,
    updateEmail,
    confirmNewEmail,
    updateConfirmNewEmailChangeEmail,
    insertEmailChangeEmailRequestStatus,
    handleSubmitInsertEmail,

    changeEmailVerificationCodeEmail,
    changeEmailVerificationCodePhoneNumber,
    updateVerificationCodeEmail,
    updateVerificationCodePhoneNumber,
    verificationEmailChangeEmailRequestStatus,
    handleSubmitVerificationEmail,

    step,
    openDialogChangeEmail,

    tooManyAttemptsErrorChangeEmail,
    handleCloseErrorTooManyAttemptsChangeEmail,
    closeStep3ChangeEmail,
    prepareChangeContactRequestStatus,

    closeChangeEmail,
    onChangeEmail,

    backToStep1,
    backToStep2,

    errorEmailNotEditable,
    emailAlreadyVerificated,
    closeErrorEmailNotEditable,
    buttonDisabled,
    countdown,
    timeoutError,

    changeEmailDisabled,
  };
};
